import toast from 'react-hot-toast'

import InfoBox from '@/components/InfoBox'
import { ShipperMultiSelect } from '@/components/inputs/ShipperMultiSelect'
import { useLoad } from '@/hooks/shipments'

import {
  useAccountTagHelpers,
  useShipmentTagMutations,
} from './useShipmentTagMutations'
import { type ShipmentTagOption, useTagSettings } from './useShipmentTags'

type ShipmentTagsControlProps = {
  loadId: string
}

const showPermError = (actionType: 'create' | 'modify') => {
  toast.error(`You do not have the required permissions to ${actionType} tags.`)
}

export const ShipmentTagsControl = ({ loadId }: ShipmentTagsControlProps) => {
  const { addTag, isAdding, isRemoving, removeTag } =
    useShipmentTagMutations(loadId)
  const loadQuery = useLoad(loadId)

  return loadQuery.isLoading && loadQuery.isFetching ? (
    <div>
      <InfoBox icon="spinner">Loading...</InfoBox>
    </div>
  ) : (
    <TagSelector
      isLoading={isAdding || isRemoving}
      tagNames={loadQuery.data?.tags?.map(t => t.name) ?? []}
      onAdd={addTag}
      onRemove={removeTag}
    />
  )
}

type TagSelectorProps = {
  onAdd: (option: ShipmentTagOption) => void
  onRemove: (option: ShipmentTagOption) => void
  isLoading: boolean
  tagIds?: string[]
  tagNames?: string[]
}

export const TagSelector = ({
  onAdd,
  onRemove,
  isLoading,
  tagIds,
  tagNames,
}: TagSelectorProps) => {
  const {
    canCreate,
    canModify,
    isLoading: tagsLoading,
    options,
  } = useTagSettings()
  const { modifyAccountTags } = useAccountTagHelpers()

  const value: ShipmentTagOption[] = tagNames?.length
    ? options.filter(o => tagNames.includes(o.label))
    : (tagIds
        ?.map(id => options.find(o => o.value === id))
        .filter((o): o is ShipmentTagOption => !!o) ?? [])

  return (
    <ShipperMultiSelect
      options={options}
      value={value}
      showTrigger
      onChange={nextValues => {
        if (!canModify) {
          showPermError('modify')
          return
        }

        const added = nextValues.find(
          v => !value.some(existing => existing.value === v.value),
        )
        const removed = value.find(
          v => !nextValues.some(next => next.value === v.value),
        )

        if (added) onAdd(added)
        if (removed) onRemove(removed)
      }}
      onCreate={inputValue => {
        if (!canModify) {
          showPermError('modify')
          return
        }
        if (!canCreate) {
          showPermError('create')
          return
        }
        const newOption: ShipmentTagOption = {
          label: inputValue,
          value: inputValue,
        }
        modifyAccountTags(
          tags =>
            tags
              ?.filter(t => t._id !== newOption.value)
              .concat({ _id: newOption.value, label: newOption.label }) || [],
        )
        onAdd(newOption)
      }}
      isLoading={isLoading || tagsLoading}
      disabled={!canModify}
      getOptionLabel={o => o.label}
      getOptionValue={o => o.value}
      isValidNewOption={input => !!input?.trim() && !!canCreate}
      classNames={{
        container: 'w-full',
      }}
    />
  )
}
